import { useCallback, useEffect, useMemo, useState } from "react";
import { PageHeader } from "@/components/ui/PageHeader";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { NativeSelect, NativeSelectOption } from "@/components/ui/native-select";
import { ToggleGroup, ToggleGroupCount, ToggleGroupItem } from "@/components/ui/toggle-group";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  AvatarIniciales,
  AvisoNavy,
  CeldaPrincipal,
  DataTable,
  Icon,
  KpiCard,
  KpiGrid,
  StatusBadge,
  TarjetasOpcion,
  Tip,
  type EstadoDS,
} from "@/components/ds";
import {
  FormDrawer,
  FormDrawerClose,
  FormDrawerContent,
  FormDrawerDescription,
  FormDrawerFooter,
  FormDrawerFooterHint,
  FormDrawerHeader,
  FormDrawerTitle,
  formDrawerBodyClass,
} from "@/components/ds/FormDrawer";
import { AlertCircle, Loader2, Search } from "@/components/ds/icons";
import { toast } from "sonner";
import { api, ApiError } from "@/lib/api";
import { cleanRut } from "@/lib/rut";
import { formatNombreArea, ordenarAreas } from "@/lib/area";
import type { AreaSummary } from "@/types/area";

// ─── Vocabulario ─────────────────────────────────────────────────────────────

/**
 * Los tres estados los decide el SERVIDOR, no esta pantalla.
 *
 * El binario "tiene licencia / no tiene" escondía el caso que cuesta plata: la
 * invitación pendiente ocupa el cupo igual que un usuario activo, pero ese
 * trabajador todavía no puede entrar a la app. Contarlos juntos hacía que la
 * empresa se quedara sin licencias sin entender por qué.
 */
type EstadoLicencia = "activo" | "invitado" | "sin_acceso";

type FuenteLicencia = "propia" | "mandante";

interface CargoMin {
  id: number;
  nombre: string;
}

interface MandanteMin {
  id: number;
  nombre: string | null;
}

interface LicenciaInfo {
  activa: boolean;
  estado: EstadoLicencia;
  fuente: FuenteLicencia | null;
  mandante: MandanteMin | null;
  /** Sólo viene con la invitación pendiente. */
  invitacion_enviada_at: string | null;
  /** Cuándo caduca el enlace de primer ingreso. */
  invitacion_vence_at: string | null;
  /** Última vez que tocó la app móvil de esta empresa. */
  ultima_actividad_at: string | null;
}

interface Trabajador {
  id: number;
  nombre: string;
  apellido: string;
  rut: string;
  email: string | null;
  iniciales: string;
  activo: boolean;
  cargo: CargoMin | null;
  areas: AreaSummary[];
  licencia: LicenciaInfo;
}

interface OrigenCesion {
  id: number;
  nombre: string | null;
  total: number;
  usadas: number;
}

interface AsignacionData {
  propias: { total: number; usadas: number; cedidas: number; disponibles: number };
  mandantes: {
    total: number;
    usadas: number;
    disponibles: number;
    origenes: OrigenCesion[];
  };
  invitacion: { vigencia_dias: number };
  trabajadores: Trabajador[];
}

/**
 * Las cuatro vistas del listado. Se pliegan del MISMO padrón, así que el
 * número del riel y el de la tarjeta no pueden discrepar.
 */
type Vista = "todos" | "pendientes" | "con_acceso" | "sin_acceso";

// ─── Helpers ─────────────────────────────────────────────────────────────────

function nombreCompleto(t: Trabajador): string {
  return `${t.nombre} ${t.apellido}`.trim();
}

function plural(n: number, singular: string, plural: string): string {
  return `${n} ${n === 1 ? singular : plural}`;
}

/**
 * Estas fechas vienen del servidor en ISO-8601 CON huso (`toIso8601String`),
 * así que `new Date(iso)` es seguro acá. El off-by-one de zona horaria que
 * documenta `lib/vencimiento.ts` aplica sólo a las fechas *date-only*
 * (`YYYY-MM-DD`), que no es el caso de ninguna de éstas.
 */
function diasDesde(iso: string): number {
  return Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000));
}

function diasHasta(iso: string): number {
  return Math.max(0, Math.ceil((new Date(iso).getTime() - Date.now()) / 86_400_000));
}

function fechaCorta(iso: string): string {
  return new Date(iso).toLocaleDateString("es-CL", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

/** ¿El enlace de primer ingreso ya caducó? */
function invitacionVencida(t: Trabajador): boolean {
  const vence = t.licencia.invitacion_vence_at;
  return vence !== null && new Date(vence).getTime() <= Date.now();
}

/**
 * Mapa único estado del dominio → estado canónico del Design System. Vive acá
 * y no repartido por las celdas para que el color de un estado sea uno solo en
 * toda la pantalla: la fila, la tarjeta y el riel dicen lo mismo.
 *
 * `no_asignado` para "Sin acceso" no es un parche: su significado en el DS es
 * exactamente "no existe la fila de asignación", que es este caso.
 */
function estadoDS(t: Trabajador): { estado: EstadoDS; etiqueta: string } {
  if (t.licencia.estado === "activo") return { estado: "activo", etiqueta: "Con acceso" };
  if (t.licencia.estado === "invitado") {
    return invitacionVencida(t)
      ? { estado: "expirado", etiqueta: "Invitación vencida" }
      : { estado: "iniciado", etiqueta: "Invitación pendiente" };
  }
  return { estado: "no_asignado", etiqueta: "Sin acceso" };
}

const EMAIL_VALIDO = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

// ─── Celdas y tarjetas ───────────────────────────────────────────────────────

/**
 * Cupos propios: cuántos hay tomados y cuántos de ésos están RESERVADOS por
 * una invitación que nadie usó todavía.
 *
 * La barra se dibuja acá y no con `ProgressBar` del DS por dos razones. La
 * primera es que necesita dos segmentos. La segunda es que `ProgressBar`
 * colorea por umbral porque mide CUMPLIMIENTO, y acá el número es una
 * capacidad: gastar el 90% de las licencias no es "rojo" ni tener el 10%
 * ocupado es "verde". El color de marca sólo da magnitud.
 */
function TarjetaCupos({
  total,
  activas,
  invitadas,
  cedidas,
}: {
  total: number;
  activas: number;
  invitadas: number;
  cedidas: number;
}) {
  const usadas = activas + invitadas;
  const libres = Math.max(0, total - usadas - cedidas);
  const pct = (n: number) => (total > 0 ? Math.min(100, (n / total) * 100) : 0);

  const detalle =
    total === 0
      ? "tu plan todavía no incluye cupos de app"
      : `${plural(libres, "libre", "libres")}` +
        (invitadas > 0 ? ` · ${invitadas} reservadas por invitaciones` : "") +
        (cedidas > 0 ? ` · ${cedidas} cedidas a otras empresas` : "");

  return (
    <div className="bg-card flex flex-col gap-1.5 rounded-xl border px-4 py-3.5">
      <span className="text-muted-foreground text-xs">Licencias propias en uso</span>
      <span className={`fc-kpi-value ${libres === 0 && total > 0 ? "text-warning" : ""}`}>
        <span className="fc-num">{usadas}</span>
        <span className="text-soft">/{total}</span>
      </span>
      <div
        className="bg-border-subtle flex h-1.5 w-full overflow-hidden rounded-full"
        role="progressbar"
        aria-valuenow={usadas}
        aria-valuemin={0}
        aria-valuemax={total}
        aria-label={`${usadas} de ${total} licencias propias en uso`}
      >
        <div className="bg-primary h-full" style={{ width: `${pct(activas)}%` }} />
        {/* Ámbar, el mismo tono que el badge de "Invitación pendiente": es el
            cupo gastado que todavía no le sirve a nadie. */}
        <div className="bg-warning h-full" style={{ width: `${pct(invitadas)}%` }} />
      </div>
      <span className="text-soft text-[11px] leading-[1.35]">{detalle}</span>
    </div>
  );
}

/** Estado del acceso y, cuando hay invitación, si toca esperar o reenviarla. */
function CeldaAcceso({ t }: { t: Trabajador }) {
  const { estado, etiqueta } = estadoDS(t);

  let meta = t.cargo?.nombre ?? "Sin cargo";
  let metaClase = "text-soft";

  if (t.licencia.estado === "invitado") {
    const vence = t.licencia.invitacion_vence_at;
    if (vence === null) {
      meta = "aún no crea su contraseña";
      metaClase = "text-warning";
    } else if (invitacionVencida(t)) {
      meta = "el enlace venció · reenvíala";
      metaClase = "text-destructive";
    } else {
      // Sin repetir "aún no crea su contraseña": eso ya lo dice el badge de
      // arriba, y la línea completa no entra en la columna — se truncaba justo
      // en el dato que falta, que es cuándo vence.
      meta = `vence en ${plural(diasHasta(vence), "día", "días")}`;
      metaClase = "text-warning";
    }
  } else if (t.licencia.estado === "sin_acceso" && !t.email) {
    meta = "sin correo no se le puede invitar";
    metaClase = "text-warning";
  }

  return (
    <div className="flex min-w-0 flex-col gap-0.5 pr-3">
      <StatusBadge estado={estado}>{etiqueta}</StatusBadge>
      <span className={`fc-truncate text-[10.5px] ${metaClase}`}>{meta}</span>
    </div>
  );
}

/**
 * Última actividad, NO último ingreso: sale de `personal_access_tokens
 * .last_used_at`, que Sanctum refresca en cada request de la app. Un
 * trabajador que dejó la sesión abierta y sincroniza a diario nunca vuelve a
 * pasar por el login, así que "último ingreso" diría que hace meses que no usa
 * la app cuando la usó hoy.
 */
function CeldaActividad({ t }: { t: Trabajador }) {
  const iso = t.licencia.ultima_actividad_at;

  if (!iso) {
    return (
      <div className="flex min-w-0 flex-col gap-0.5 pr-3">
        <span className="text-soft text-[12px]">
          {t.licencia.estado === "sin_acceso" ? "—" : "Nunca"}
        </span>
        {t.licencia.estado !== "sin_acceso" && (
          <span className="text-soft text-[10.5px]">no ha entrado a la app</span>
        )}
      </div>
    );
  }

  const dias = diasDesde(iso);
  return (
    <div className="flex min-w-0 flex-col gap-0.5 pr-3">
      <span className="fc-num text-[12px]">{fechaCorta(iso)}</span>
      <span className="text-soft text-[10.5px]">
        {dias === 0 ? "hoy" : `hace ${plural(dias, "día", "días")}`}
      </span>
    </div>
  );
}

/** De qué bolsa salió el cupo. Un préstamo tiene dueño y conviene verlo. */
function CeldaFuente({ t }: { t: Trabajador }) {
  if (t.licencia.fuente === "propia") {
    return <Badge variant="brand">Mi empresa</Badge>;
  }
  if (t.licencia.fuente === "mandante") {
    return (
      // `shrink` y no el `shrink-0` que el Badge trae por defecto: el nombre de
      // un mandante puede ser largo y desbordaría la celda en vez de truncarse.
      <Badge variant="navy" className="min-w-0 shrink">
        <span className="truncate">{t.licencia.mandante?.nombre ?? "Mandante"}</span>
      </Badge>
    );
  }
  return <span className="text-soft text-[12px]">—</span>;
}

// ─── Pantalla ────────────────────────────────────────────────────────────────

export function AsignacionUsuarios() {
  const [data, setData] = useState<AsignacionData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [vista, setVista] = useState<Vista>("todos");
  const [search, setSearch] = useState("");
  const [areaId, setAreaId] = useState("");
  const [seleccion, setSeleccion] = useState<number[]>([]);

  const [drawerAbierto, setDrawerAbierto] = useState(false);
  const [pedirCorreoA, setPedirCorreoA] = useState<Trabajador | null>(null);
  const [aRevocar, setARevocar] = useState<Trabajador[] | null>(null);
  const [sinCupos, setSinCupos] = useState(false);

  /** id de la fila en vuelo, o `"lote"` para las acciones masivas. */
  const [enVuelo, setEnVuelo] = useState<number | "lote" | null>(null);

  const cargar = useCallback(async () => {
    setIsLoading(true);
    setLoadError(null);
    try {
      const res = await api.get<{ data: AsignacionData }>("/me/licencias/asignacion");
      setData(res.data);
    } catch {
      setLoadError("No se pudo cargar el listado de licencias.");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    cargar();
  }, [cargar]);

  const trabajadores = useMemo(() => data?.trabajadores ?? [], [data]);
  const propias = data?.propias ?? { total: 0, usadas: 0, cedidas: 0, disponibles: 0 };
  const mandantes = data?.mandantes ?? { total: 0, usadas: 0, disponibles: 0, origenes: [] };
  const diasInvitacion = data?.invitacion.vigencia_dias ?? 7;

  // ── Derivados del padrón ──────────────────────────────────────────────────

  const conAcceso = useMemo(
    () => trabajadores.filter((t) => t.licencia.estado === "activo"),
    [trabajadores],
  );
  const pendientes = useMemo(
    () => trabajadores.filter((t) => t.licencia.estado === "invitado"),
    [trabajadores],
  );
  const sinAcceso = useMemo(
    () => trabajadores.filter((t) => t.licencia.estado === "sin_acceso"),
    [trabajadores],
  );
  const sinCorreo = useMemo(() => sinAcceso.filter((t) => !t.email), [sinAcceso]);
  const vencidas = useMemo(() => pendientes.filter(invitacionVencida), [pendientes]);

  /** Cupos libres en cada bolsa. Es el techo de cualquier asignación. */
  const cuposPropios = propias.disponibles;
  const cuposMandante = mandantes.disponibles;
  const cuposTotales = cuposPropios + cuposMandante;

  /**
   * Catálogo de áreas deducido del propio padrón. No se pide `/areas` aparte:
   * filtrar por un área en la que no hay nadie sólo produce una tabla vacía.
   */
  const areas = useMemo(() => {
    const porId = new Map<number, AreaSummary>();
    for (const t of trabajadores) {
      for (const a of t.areas) porId.set(a.id, a);
    }
    return ordenarAreas([...porId.values()]);
  }, [trabajadores]);

  const filas = useMemo(() => {
    const base =
      vista === "todos"
        ? trabajadores
        : vista === "pendientes"
          ? pendientes
          : vista === "con_acceso"
            ? conAcceso
            : sinAcceso;

    const q = search.trim().toLowerCase();
    const rutQuery = q ? cleanRut(q) : "";
    const area = areaId ? Number(areaId) : null;

    return base.filter((t) => {
      if (area !== null && !t.areas.some((a) => a.id === area)) return false;
      if (!q) return true;
      return (
        nombreCompleto(t).toLowerCase().includes(q) ||
        (rutQuery !== "" && t.rut.toLowerCase().includes(rutQuery)) ||
        (t.cargo?.nombre ?? "").toLowerCase().includes(q) ||
        (t.email ?? "").toLowerCase().includes(q)
      );
    });
  }, [vista, trabajadores, pendientes, conAcceso, sinAcceso, search, areaId]);

  const hayFiltro = search.trim() !== "" || areaId !== "";

  // ── Selección ─────────────────────────────────────────────────────────────

  /**
   * La selección se mide contra las filas FILTRADAS, no contra la página: la
   * paginación vive dentro de `DataTable` y esta pantalla no la conoce. Es
   * además lo que la persona espera después de filtrar por "Sin acceso" para
   * darles acceso a todos de una.
   */
  const idsFiltrados = useMemo(() => filas.map((t) => t.id), [filas]);

  const seleccionados = useMemo(
    () => trabajadores.filter((t) => seleccion.includes(t.id)),
    [trabajadores, seleccion],
  );

  /** Sólo se les puede dar acceso a los que no lo tienen Y tienen correo. */
  const aAsignar = useMemo(
    () => seleccionados.filter((t) => t.licencia.estado === "sin_acceso" && !!t.email),
    [seleccionados],
  );
  const aQuitar = useMemo(
    () => seleccionados.filter((t) => t.licencia.estado !== "sin_acceso"),
    [seleccionados],
  );

  /**
   * Seleccionados a los que NINGUNA de las dos acciones alcanza: hoy es sólo
   * el caso "sin acceso y sin correo". Se cuenta para poder decirlo — que la
   * barra diga "3 seleccionados" y actúe sobre 2 sin explicar por qué es
   * exactamente el tipo de silencio que hace desconfiar de una acción masiva.
   */
  const sinAccionPosible = seleccionados.length - aAsignar.length - aQuitar.length;

  const todosSeleccionados =
    idsFiltrados.length > 0 && idsFiltrados.every((id) => seleccion.includes(id));
  const algunoSeleccionado = idsFiltrados.some((id) => seleccion.includes(id));

  const alternarTodos = () =>
    setSeleccion((prev) =>
      todosSeleccionados
        ? prev.filter((id) => !idsFiltrados.includes(id))
        : [...new Set([...prev, ...idsFiltrados])],
    );

  const alternarUno = (id: number) =>
    setSeleccion((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const limpiarSeleccion = () => setSeleccion([]);

  const limpiarFiltros = () => {
    setSearch("");
    setAreaId("");
  };

  // ── Acciones ──────────────────────────────────────────────────────────────

  /**
   * Asigna UNA licencia. Devuelve `null` si salió bien, `"sin_invitacion"` si
   * la cuenta ya existía (y por lo tanto no se mandó correo), `"quota"` si no
   * quedan cupos, o el mensaje del servidor. El lote necesita distinguirlos
   * para contar sin tragarse el motivo.
   */
  const asignarUno = async (
    t: Trabajador,
    opciones: { email?: string; fuente?: FuenteLicencia } = {},
  ): Promise<string | null> => {
    try {
      const res = await api.post<{ data: Trabajador; invitacion_enviada: boolean }>(
        `/me/licencias/asignacion/${t.id}`,
        opciones,
      );
      return res.invitacion_enviada ? null : "sin_invitacion";
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.status === 422 && (err.data as { quota_exceeded?: boolean })?.quota_exceeded) {
          return "quota";
        }
        const primero = err.errors ? Object.values(err.errors)[0]?.[0] : undefined;
        return primero ?? err.message;
      }
      return "No se pudo asignar la licencia.";
    }
  };

  const asignar = async (
    t: Trabajador,
    opciones: { email?: string; fuente?: FuenteLicencia } = {},
  ) => {
    setEnVuelo(t.id);
    const error = await asignarUno(t, opciones);
    setEnVuelo(null);

    if (error === "quota") {
      setSinCupos(true);
      return;
    }
    if (error !== null && error !== "sin_invitacion") {
      toast.error(error);
      return;
    }

    setPedirCorreoA(null);
    if (error === "sin_invitacion") {
      // El User ya existía (acceso web, otra empresa, una asignación previa):
      // ya tiene contraseña, así que mandarle "crea tu cuenta" sólo confunde.
      toast.success(`${t.nombre} ya puede entrar a la app con su contraseña actual.`);
    } else {
      toast.success(
        `Invitación enviada a ${opciones.email ?? t.email}. El enlace vence en ${plural(diasInvitacion, "día", "días")}.`,
      );
    }
    await cargar();
  };

  /**
   * Lote. No hay endpoint masivo: son N requests secuenciales contra el mismo
   * endpoint ya validado de a uno. Secuenciales y no en paralelo a propósito —
   * los cupos se toman con `lockForUpdate`, y disparar veinte a la vez sólo
   * cambiaría un rechazo ordenado por una carrera.
   *
   * El techo de cupos se revisa ANTES de mandar nada: cortar a mitad de camino
   * dejaría media selección adentro y media afuera sin decir cuál fue cuál.
   */
  const asignarLote = async (elegidos: Trabajador[], fuente: FuenteLicencia) => {
    const cupos = fuente === "propia" ? cuposPropios : cuposMandante;
    if (elegidos.length > cupos) {
      toast.error(
        `No alcanzan las licencias: elegiste ${elegidos.length} y quedan ${cupos} en esa bolsa.`,
      );
      return;
    }

    setEnVuelo("lote");
    let ok = 0;
    const errores: string[] = [];
    for (const t of elegidos) {
      const error = await asignarUno(t, { fuente });
      if (error === null || error === "sin_invitacion") ok++;
      else errores.push(`${nombreCompleto(t)}: ${error === "quota" ? "sin cupos" : error}`);
    }
    setEnVuelo(null);

    if (ok > 0) {
      toast.success(
        ok === 1
          ? `1 invitación enviada. El enlace vence en ${plural(diasInvitacion, "día", "días")}.`
          : `${ok} invitaciones enviadas. Cada enlace vence en ${plural(diasInvitacion, "día", "días")}.`,
      );
    }
    if (errores.length > 0) {
      toast.error(
        `${plural(errores.length, "trabajador quedó", "trabajadores quedaron")} sin licencia. ${errores[0]}`,
      );
    }

    setDrawerAbierto(false);
    limpiarSeleccion();
    await cargar();
  };

  const revocar = async (elegidos: Trabajador[]) => {
    setEnVuelo(elegidos.length === 1 ? elegidos[0].id : "lote");
    let ok = 0;
    for (const t of elegidos) {
      try {
        await api.delete(`/me/licencias/asignacion/${t.id}`);
        ok++;
      } catch {
        // Se reporta abajo con el conteo: un toast por cada fallo taparía la
        // pantalla justo cuando hay que entender qué quedó a medias.
      }
    }
    setEnVuelo(null);
    setARevocar(null);

    if (ok > 0) {
      toast.success(
        ok === 1
          ? "Acceso revocado. La licencia volvió a la bolsa y el historial se conserva."
          : `${ok} accesos revocados. Sus licencias volvieron a la bolsa.`,
      );
    }
    if (ok < elegidos.length) {
      toast.error(`${elegidos.length - ok} no se pudieron revocar. Vuelve a intentarlo.`);
    }

    limpiarSeleccion();
    await cargar();
  };

  const reenviarInvitacion = async (t: Trabajador) => {
    setEnVuelo(t.id);
    try {
      await api.post(`/me/licencias/asignacion/${t.id}/reenviar-invitacion`, {});
      toast.success(
        `Invitación reenviada a ${t.email}. El enlace anterior deja de servir y el nuevo vence en ${plural(diasInvitacion, "día", "días")}.`,
      );
      await cargar();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "No se pudo reenviar la invitación.");
    } finally {
      setEnVuelo(null);
    }
  };

  const restablecerPassword = async (t: Trabajador) => {
    setEnVuelo(t.id);
    try {
      await api.post(`/me/licencias/asignacion/${t.id}/restablecer-password`, {});
      toast.success(`Le enviamos a ${t.email} un correo para crear una contraseña nueva.`);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "No se pudo enviar el restablecimiento.");
    } finally {
      setEnVuelo(null);
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────

  const mensajeVacio = hayFiltro
    ? "Ningún trabajador coincide con los filtros."
    : vista === "pendientes"
      ? "No hay invitaciones sin aceptar."
      : vista === "con_acceso"
        ? "Todavía nadie tiene acceso a la app."
        : vista === "sin_acceso"
          ? "Todos los trabajadores tienen acceso."
          : "Todavía no hay trabajadores en la dotación.";

  return (
    <div className="space-y-6">
      <PageHeader
        title="Asignación de usuarios"
        subtitle="Al asignar una licencia se crea la cuenta del trabajador y le llega un correo."
        rightContent={
          // Sin `disabled` cuando no quedan cupos: el botón se ve inerte pero
          // abre igual, y el panel explica de qué bolsa falta espacio. Un botón
          // muerto no explica nada.
          <Button
            onClick={() => setDrawerAbierto(true)}
            className={cuposTotales > 0 ? "shrink-0" : "shrink-0 opacity-50"}
          >
            <Icon name="person_add" size={16} />
            Asignar licencias
          </Button>
        }
      />

      <KpiGrid min={210}>
        <TarjetaCupos
          total={propias.total}
          activas={conAcceso.filter((t) => t.licencia.fuente === "propia").length}
          invitadas={pendientes.filter((t) => t.licencia.fuente === "propia").length}
          cedidas={propias.cedidas}
        />
        <KpiCard
          label="Invitaciones sin aceptar"
          valor={pendientes.length}
          alerta={vencidas.length > 0}
          detalle={
            pendientes.length === 0
              ? "nadie tiene el correo pendiente"
              : vencidas.length > 0
                ? `${plural(vencidas.length, "enlace vencido", "enlaces vencidos")} · el cupo sigue ocupado`
                : "el cupo sigue ocupado mientras tanto"
          }
          onClick={() => {
            setVista("pendientes");
            limpiarFiltros();
          }}
        />
        <KpiCard
          label="Licencias de mandantes"
          valor={`${mandantes.usadas}/${mandantes.total}`}
          detalle={
            mandantes.total === 0
              ? "ningún mandante te cedió licencias"
              : `cedidas por ${mandantes.origenes.map((o) => o.nombre ?? "un mandante").join(" · ")}`
          }
        />
        <KpiCard
          label="Trabajadores sin acceso"
          valor={sinAcceso.length}
          detalle={
            sinCorreo.length > 0
              ? `${sinCorreo.length} sin correo registrado`
              : `de ${plural(trabajadores.length, "trabajador", "trabajadores")} en la dotación`
          }
          onClick={() => {
            setVista("sin_acceso");
            limpiarFiltros();
          }}
        />
      </KpiGrid>

      {isLoading ? (
        <Card>
          <CardContent className="flex items-center justify-center py-12 pt-6">
            <Loader2 className="text-muted-foreground h-6 w-6 animate-spin" />
          </CardContent>
        </Card>
      ) : loadError ? (
        <Card>
          <CardContent className="flex flex-col items-center gap-3 py-12 pt-6 text-center">
            <AlertCircle className="text-destructive h-6 w-6" />
            <p className="text-muted-foreground text-sm">{loadError}</p>
            <button type="button" onClick={cargar} className="text-primary-hover text-sm underline">
              Reintentar
            </button>
          </CardContent>
        </Card>
      ) : (
        <DataTable
          rows={filas}
          getRowKey={(t) => t.id}
          // 34+240+180+150+140+120 = 864px de columnas + 28px del `px-3.5` de la
          // fila. Por debajo de esa suma la grilla se desborda en vez de
          // scrollear, y la cabecera deja de alinearse con las filas.
          minWidth={892}
          pageSize={20}
          emptyMessage={mensajeVacio}
          emptyIcon={hayFiltro ? "search_off" : "manage_accounts"}
          emptyAction={
            hayFiltro ? (
              <Button variant="outline" size="sm" onClick={limpiarFiltros}>
                Quitar filtros
              </Button>
            ) : undefined
          }
          toolbar={
            <>
              <ToggleGroup
                type="single"
                variant="segmented"
                value={vista}
                onValueChange={(v) => {
                  if (!v) return;
                  setVista(v as Vista);
                  limpiarSeleccion();
                }}
              >
                <ToggleGroupItem value="todos">
                  Todos
                  <ToggleGroupCount>{trabajadores.length}</ToggleGroupCount>
                </ToggleGroupItem>
                <ToggleGroupItem value="pendientes">
                  Pendientes
                  <ToggleGroupCount>{pendientes.length}</ToggleGroupCount>
                </ToggleGroupItem>
                <ToggleGroupItem value="con_acceso">
                  Con acceso
                  <ToggleGroupCount>{conAcceso.length}</ToggleGroupCount>
                </ToggleGroupItem>
                <ToggleGroupItem value="sin_acceso">
                  Sin acceso
                  <ToggleGroupCount>{sinAcceso.length}</ToggleGroupCount>
                </ToggleGroupItem>
              </ToggleGroup>

              <div className="relative min-w-[190px] flex-1">
                <Search className="text-soft absolute top-1/2 left-2.5 h-4 w-4 -translate-y-1/2" />
                <Input
                  placeholder="Buscar por nombre, RUT, cargo o correo…"
                  className="h-9 pl-8 text-[13px]"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>

              {/* Las áreas son un clasificador OPCIONAL: si la empresa no las
                  usa, el filtro no aparece en vez de ofrecer un select vacío. */}
              {areas.length > 0 && (
                <NativeSelect
                  aria-label="Filtrar por área"
                  value={areaId}
                  onChange={(e) => setAreaId(e.target.value)}
                >
                  <NativeSelectOption value="">Todas las áreas</NativeSelectOption>
                  {areas.map((a) => (
                    <NativeSelectOption key={a.id} value={String(a.id)}>
                      {formatNombreArea(a)}
                    </NativeSelectOption>
                  ))}
                </NativeSelect>
              )}

              {seleccion.length > 0 && (
                // `grow basis-full` y no `flex-1`: la barra de selección tiene
                // que tomar su propia línea dentro de la toolbar, y `flex-1`
                // fija `basis:0%` — que pisa el `basis-full` y la deja
                // compartiendo fila con los filtros.
                <div className="bg-primary-50 border-primary-border grow basis-full rounded-lg border px-3 py-2">
                  <div className="flex flex-wrap items-center gap-2">
                    <Icon name="check_circle" size={17} className="text-primary-hover" />
                    <span className="text-primary-hover text-[12.5px] font-semibold">
                      {plural(seleccion.length, "seleccionado", "seleccionados")}
                    </span>
                    <span className="text-primary-hover min-w-[80px] flex-1 text-[11.5px]">
                      {sinAccionPosible > 0
                        ? `${sinAccionPosible} sin correo ${sinAccionPosible === 1 ? "queda" : "quedan"} fuera de ambas acciones.`
                        : aAsignar.length > 0 && aQuitar.length > 0
                          ? "Cada acción se aplica sólo a quien corresponde."
                          : aAsignar.length > 0
                            ? "Se les crea la cuenta y reciben el correo de primer ingreso."
                            : "Pierden el acceso a la app y su licencia se libera."}
                    </span>
                    <Button
                      variant="outline"
                      size="xs"
                      disabled={aAsignar.length === 0 || enVuelo !== null}
                      onClick={() =>
                        asignarLote(
                          aAsignar,
                          cuposPropios >= aAsignar.length ? "propia" : "mandante",
                        )
                      }
                    >
                      {enVuelo === "lote" ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Icon name="person_add" size={15} />
                      )}
                      {aAsignar.length > 0 ? `Dar acceso a ${aAsignar.length}` : "Dar acceso"}
                    </Button>
                    <Button
                      variant="outline"
                      size="xs"
                      className="text-destructive hover:text-destructive"
                      disabled={aQuitar.length === 0 || enVuelo !== null}
                      onClick={() => setARevocar(aQuitar)}
                    >
                      <Icon name="block" size={15} />
                      {aQuitar.length > 0 ? `Revocar ${aQuitar.length}` : "Revocar"}
                    </Button>
                    <Button variant="ghost" size="xs" onClick={limpiarSeleccion}>
                      Quitar selección
                    </Button>
                  </div>
                </div>
              )}
            </>
          }
          columns={[
            {
              key: "seleccion",
              // Sin `srOnlyHeader`: `sr-only` es `position:absolute`, y sobre la
              // PRIMERA celda de la grilla sacaría la celda del flujo y correría
              // TODAS las columnas siguientes — ver el comentario en DataTable.
              header: (
                <Tip texto="Seleccionar todo lo filtrado">
                  <span className="inline-flex">
                    <Checkbox
                      aria-label="Seleccionar todos los trabajadores filtrados"
                      checked={
                        todosSeleccionados ? true : algunoSeleccionado ? "indeterminate" : false
                      }
                      onCheckedChange={alternarTodos}
                    />
                  </span>
                </Tip>
              ),
              width: "34px",
              cell: (t) => (
                <Checkbox
                  aria-label={`Seleccionar a ${nombreCompleto(t)}`}
                  checked={seleccion.includes(t.id)}
                  onCheckedChange={() => alternarUno(t.id)}
                />
              ),
            },
            {
              key: "trabajador",
              header: "Trabajador",
              width: "minmax(240px,2fr)",
              cell: (t) => (
                <div className="flex min-w-0 items-center gap-2.5">
                  <AvatarIniciales
                    iniciales={t.iniciales}
                    tono={t.licencia.estado === "sin_acceso" ? "neutro" : "teal"}
                  />
                  <CeldaPrincipal
                    titulo={nombreCompleto(t)}
                    meta={
                      t.email ? t.email : <span className="text-warning">Sin correo registrado</span>
                    }
                  />
                </div>
              ),
            },
            {
              key: "acceso",
              header: "Acceso",
              width: "180px",
              cell: (t) => <CeldaAcceso t={t} />,
            },
            {
              key: "actividad",
              header: "Última actividad",
              width: "150px",
              cell: (t) => <CeldaActividad t={t} />,
            },
            {
              key: "fuente",
              header: "Licencia",
              width: "140px",
              cell: (t) => <CeldaFuente t={t} />,
            },
            {
              key: "acciones",
              header: "",
              width: "120px",
              align: "right",
              cell: (t) => (
                <FilaAcciones
                  t={t}
                  cargando={enVuelo === t.id}
                  bloqueado={enVuelo !== null}
                  onDarAcceso={() => (t.email ? asignar(t) : setPedirCorreoA(t))}
                  onPedirCorreo={() => setPedirCorreoA(t)}
                  onReenviar={() => reenviarInvitacion(t)}
                  onRestablecer={() => restablecerPassword(t)}
                  onRevocar={() => setARevocar([t])}
                />
              ),
            },
          ]}
        />
      )}

      <FormDrawer open={drawerAbierto} onOpenChange={setDrawerAbierto} side="right">
        <DrawerAsignacion
          key={drawerAbierto ? "abierto" : "cerrado"}
          candidatos={sinAcceso}
          cuposPropios={cuposPropios}
          cuposMandante={cuposMandante}
          totalPropias={propias.total}
          origenes={mandantes.origenes}
          diasInvitacion={diasInvitacion}
          enVuelo={enVuelo === "lote"}
          onEnviar={asignarLote}
        />
      </FormDrawer>

      <DialogoCorreo
        trabajador={pedirCorreoA}
        cargando={enVuelo !== null}
        diasInvitacion={diasInvitacion}
        onCancelar={() => setPedirCorreoA(null)}
        onConfirmar={(email) => pedirCorreoA && asignar(pedirCorreoA, { email })}
      />

      <DialogoRevocar
        elegidos={aRevocar}
        cargando={enVuelo !== null}
        onCancelar={() => setARevocar(null)}
        onConfirmar={() => aRevocar && revocar(aRevocar)}
      />

      <AlertDialog open={sinCupos} onOpenChange={setSinCupos}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Icon name="error_outline" size={20} className="text-warning" />
              No quedan licencias disponibles
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2">
                <p>
                  Tu plan tiene <strong>{plural(propias.total, "licencia", "licencias")}</strong> y
                  todas están ocupadas.
                </p>
                {pendientes.length > 0 && (
                  <p>
                    {plural(pendientes.length, "está reservada", "están reservadas")} por
                    invitaciones que nadie aceptó todavía: cancelarlas libera el cupo al instante.
                  </p>
                )}
                <p className="text-muted-foreground text-xs">
                  También puedes revocarle el acceso a quien ya no la usa, o pedir más cupos.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Entendido</AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ─── Acciones de fila ────────────────────────────────────────────────────────

interface FilaAccionesProps {
  t: Trabajador;
  cargando: boolean;
  bloqueado: boolean;
  onDarAcceso: () => void;
  onPedirCorreo: () => void;
  onReenviar: () => void;
  onRestablecer: () => void;
  onRevocar: () => void;
}

function FilaAcciones({
  t,
  cargando,
  bloqueado,
  onDarAcceso,
  onPedirCorreo,
  onReenviar,
  onRestablecer,
  onRevocar,
}: FilaAccionesProps) {
  if (cargando) {
    return <Loader2 className="text-muted-foreground h-4 w-4 animate-spin" />;
  }

  if (t.licencia.estado === "sin_acceso") {
    return t.email ? (
      <Button variant="outline" size="xs" disabled={bloqueado} onClick={onDarAcceso}>
        <Icon name="person_add" size={15} />
        Dar acceso
      </Button>
    ) : (
      <Tip texto="Agrega su correo para poder invitarlo">
        <Button
          variant="outline"
          size="xs"
          className="border-warning-border text-warning"
          disabled={bloqueado}
          onClick={onPedirCorreo}
        >
          <Icon name="mail" size={15} />
          Falta correo
        </Button>
      </Tip>
    );
  }

  const esInvitacion = t.licencia.estado === "invitado";

  return (
    <div className="flex items-center justify-end gap-0.5">
      <Tip
        texto={
          esInvitacion
            ? "Reenviar la invitación"
            : "Enviarle un correo para crear una contraseña nueva"
        }
      >
        <Button
          variant="ghost"
          size="icon-sm"
          disabled={bloqueado}
          aria-label={
            esInvitacion
              ? `Reenviar la invitación de ${nombreCompleto(t)}`
              : `Restablecer la contraseña de ${nombreCompleto(t)}`
          }
          onClick={esInvitacion ? onReenviar : onRestablecer}
        >
          <Icon name={esInvitacion ? "forward_to_inbox" : "lock_reset"} size={16} />
        </Button>
      </Tip>
      <Tip
        texto={
          esInvitacion
            ? "Cancelar la invitación y liberar la licencia"
            : "Revocar el acceso y liberar la licencia"
        }
      >
        <Button
          variant="ghost"
          size="icon-sm"
          className="text-soft hover:text-destructive"
          disabled={bloqueado}
          aria-label={
            esInvitacion
              ? `Cancelar la invitación de ${nombreCompleto(t)}`
              : `Revocar el acceso de ${nombreCompleto(t)}`
          }
          onClick={onRevocar}
        >
          <Icon name="block" size={16} />
        </Button>
      </Tip>
    </div>
  );
}

// ─── Panel de asignación en lote ─────────────────────────────────────────────

interface DrawerAsignacionProps {
  candidatos: Trabajador[];
  cuposPropios: number;
  cuposMandante: number;
  totalPropias: number;
  origenes: OrigenCesion[];
  diasInvitacion: number;
  enVuelo: boolean;
  onEnviar: (elegidos: Trabajador[], fuente: FuenteLicencia) => void;
}

function DrawerAsignacion({
  candidatos,
  cuposPropios,
  cuposMandante,
  totalPropias,
  origenes,
  diasInvitacion,
  enVuelo,
  onEnviar,
}: DrawerAsignacionProps) {
  /** Arranca en la bolsa que tenga cupos: elegir por defecto una vacía obliga
   *  a un clic que no decide nada. */
  const [fuente, setFuente] = useState<FuenteLicencia>(
    cuposPropios > 0 || cuposMandante === 0 ? "propia" : "mandante",
  );
  const [busqueda, setBusqueda] = useState("");
  const [elegidos, setElegidos] = useState<number[]>([]);

  const nombreMandantes =
    origenes.length > 0 ? origenes.map((o) => o.nombre ?? "un mandante").join(" · ") : "un mandante";

  const filtrados = useMemo(() => {
    const q = busqueda.trim().toLowerCase();
    if (!q) return candidatos;
    return candidatos.filter(
      (t) =>
        nombreCompleto(t).toLowerCase().includes(q) ||
        (t.cargo?.nombre ?? "").toLowerCase().includes(q),
    );
  }, [candidatos, busqueda]);

  const cupos = fuente === "propia" ? cuposPropios : cuposMandante;
  const seleccionados = useMemo(
    () => candidatos.filter((t) => elegidos.includes(t.id)),
    [candidatos, elegidos],
  );
  const puedeEnviar = seleccionados.length > 0 && seleccionados.length <= cupos && !enVuelo;

  const alternar = (t: Trabajador) => {
    if (!t.email) return;
    setElegidos((prev) => (prev.includes(t.id) ? prev.filter((x) => x !== t.id) : [...prev, t.id]));
  };

  /** Qué falta ANTES de intentar enviar, en vez de dejar el botón inerte. */
  const hint =
    cupos === 0
      ? fuente === "propia"
        ? "No quedan cupos propios. Libera uno o elige la bolsa del mandante."
        : "No quedan cupos cedidos. Elige la bolsa de tu empresa."
      : seleccionados.length === 0
        ? "Elige al menos un trabajador."
        : seleccionados.length > cupos
          ? `Elegiste ${seleccionados.length} y quedan ${cupos} cupos en esa bolsa.`
          : `Quedarán ${plural(cupos - seleccionados.length, "cupo", "cupos")} en esa bolsa.`;

  return (
    <FormDrawerContent>
      <FormDrawerHeader>
        <div className="flex items-center gap-3">
          <span className="bg-primary-50 text-primary-hover flex size-9 shrink-0 items-center justify-center rounded-[10px]">
            <Icon name="manage_accounts" size={20} />
          </span>
          <div className="min-w-0 flex-1">
            <FormDrawerTitle>Asignar licencias</FormDrawerTitle>
            <FormDrawerDescription>
              {plural(cuposPropios + cuposMandante, "licencia disponible", "licencias disponibles")}{" "}
              · {plural(candidatos.length, "trabajador sin acceso", "trabajadores sin acceso")}
            </FormDrawerDescription>
          </div>
        </div>
      </FormDrawerHeader>

      <div className={formDrawerBodyClass}>
        <div className="space-y-2">
          <Label className="block">De qué bolsa sale la licencia</Label>
          <TarjetasOpcion<FuenteLicencia>
            valor={fuente}
            onCambiar={setFuente}
            disabled={enVuelo}
            opciones={[
              {
                valor: "propia",
                titulo: "Licencia de mi empresa",
                descripcion: `${cuposPropios} de ${totalPropias} libres`,
                icono: "apartment",
              },
              {
                valor: "mandante",
                titulo: "Licencia del mandante",
                descripcion:
                  cuposMandante > 0
                    ? `${cuposMandante} libres, cedidas por ${nombreMandantes}`
                    : "ningún mandante te cedió licencias libres",
                icono: "domain",
              },
            ]}
          />
        </div>

        <AvisoNavy
          icono="mark_email_read"
          titulo="Qué pasa al asignar"
          detalle={`Se crea su cuenta con el correo registrado y recibe un enlace para definir su contraseña. El enlace vence en ${plural(diasInvitacion, "día", "días")} y la licencia queda ocupada mientras tanto.`}
        />

        <div className="space-y-2">
          <div className="flex items-baseline justify-between gap-3">
            <Label className="block">Trabajadores sin acceso</Label>
            <span className="text-soft fc-num text-[11px]">
              {seleccionados.length} de {cupos} cupos
            </span>
          </div>

          <div className="relative">
            <Search className="text-soft absolute top-1/2 left-2.5 h-4 w-4 -translate-y-1/2" />
            <Input
              placeholder="Filtrar por nombre o cargo…"
              className="h-9 pl-8 text-[13px]"
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              disabled={enVuelo}
            />
          </div>

          <div className="divide-border-faint divide-y overflow-hidden rounded-lg border">
            {filtrados.length === 0 ? (
              <div className="flex flex-col items-center gap-2 px-4 py-8 text-center">
                <Icon name="group" size={26} className="text-icon-soft" />
                <span className="text-muted-foreground text-[13px]">
                  {busqueda.trim()
                    ? `Nadie sin acceso coincide con “${busqueda.trim()}”.`
                    : "Todos los trabajadores ya tienen acceso."}
                </span>
              </div>
            ) : (
              filtrados.map((t) => {
                const activo = elegidos.includes(t.id);
                return (
                  <button
                    key={t.id}
                    type="button"
                    // Sin correo no hay a dónde mandar la invitación. La fila se
                    // deja visible —que exista es información— pero no se elige.
                    disabled={!t.email || enVuelo}
                    onClick={() => alternar(t)}
                    className={`flex w-full items-center gap-2.5 px-3 py-2 text-left transition-colors ${
                      activo ? "bg-primary-50" : "hover:bg-border-faint/40"
                    } disabled:cursor-not-allowed disabled:opacity-60`}
                  >
                    <Checkbox
                      checked={activo}
                      disabled={!t.email || enVuelo}
                      tabIndex={-1}
                      aria-hidden
                      className="pointer-events-none"
                    />
                    <AvatarIniciales iniciales={t.iniciales} tono={t.email ? "teal" : "neutro"} />
                    <span className="flex min-w-0 flex-1 flex-col">
                      <span className="fc-cell fc-truncate">{nombreCompleto(t)}</span>
                      <span
                        className={`fc-truncate text-[11px] ${t.email ? "text-soft" : "text-warning"}`}
                      >
                        {t.email ?? "Sin correo registrado: agrégalo en su ficha"}
                      </span>
                    </span>
                    <span className="text-soft shrink-0 text-[11px]">
                      {t.cargo?.nombre ?? "Sin cargo"}
                    </span>
                  </button>
                );
              })
            )}
          </div>
        </div>

        <FormDrawerFooter>
          <FormDrawerFooterHint>{hint}</FormDrawerFooterHint>
          <FormDrawerClose asChild>
            <Button type="button" variant="outline" disabled={enVuelo}>
              Cancelar
            </Button>
          </FormDrawerClose>
          {/* Sin `disabled` cuando falta algo: el botón se ve inerte pero sigue
              siendo clickeable, y el hint del pie dice qué falta. */}
          <Button
            type="button"
            disabled={enVuelo}
            className={puedeEnviar ? "gap-2" : "gap-2 opacity-50"}
            onClick={() => puedeEnviar && onEnviar(seleccionados, fuente)}
          >
            {enVuelo ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Icon name="send" size={16} />
            )}
            {seleccionados.length > 1
              ? `Enviar ${seleccionados.length} invitaciones`
              : "Enviar invitación"}
          </Button>
        </FormDrawerFooter>
      </div>
    </FormDrawerContent>
  );
}

// ─── Correo faltante ─────────────────────────────────────────────────────────

interface DialogoCorreoProps {
  trabajador: Trabajador | null;
  cargando: boolean;
  diasInvitacion: number;
  onCancelar: () => void;
  onConfirmar: (email: string) => void;
}

/**
 * Pedir el correo es una decisión corta que interrumpe, no un alta: por eso va
 * en diálogo y no en drawer (DS 4.8bis).
 */
function DialogoCorreo({
  trabajador,
  cargando,
  diasInvitacion,
  onCancelar,
  onConfirmar,
}: DialogoCorreoProps) {
  const [email, setEmail] = useState("");

  useEffect(() => {
    setEmail("");
  }, [trabajador?.id]);

  if (!trabajador) return null;

  const limpio = email.trim();
  const valido = EMAIL_VALIDO.test(limpio);
  const hint = !limpio
    ? "Sin correo no se puede enviar la invitación."
    : valido
      ? `Se enviará la invitación a ${limpio} y el enlace vencerá en ${plural(diasInvitacion, "día", "días")}.`
      : "Ese correo no tiene un formato válido.";

  return (
    <AlertDialog open onOpenChange={(abierto) => !abierto && onCancelar()}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Agregar el correo de {nombreCompleto(trabajador)}</AlertDialogTitle>
          <AlertDialogDescription>
            La invitación se envía a este correo, y ahí define su contraseña. Queda guardado en su
            ficha de trabajador.
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="space-y-2">
          <Label htmlFor="asignacion-email" className="block">
            Correo del trabajador
          </Label>
          <div className="relative">
            <Icon
              name="mail"
              size={16}
              className="text-soft absolute top-1/2 left-2.5 -translate-y-1/2"
            />
            <Input
              id="asignacion-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="nombre.apellido@empresa.cl"
              className="pl-8"
              disabled={cargando}
              // Sólo cuando ya escribió algo: marcar en rojo un campo vacío que
              // recién se abrió reta antes de que la persona haga nada.
              aria-invalid={limpio !== "" && !valido}
              autoFocus
            />
          </div>
          <p className={`text-[11.5px] ${limpio && !valido ? "text-destructive" : "text-soft"}`}>
            {hint}
          </p>
        </div>

        <AlertDialogFooter>
          <AlertDialogCancel disabled={cargando}>Cancelar</AlertDialogCancel>
          <AlertDialogAction
            onClick={(e) => {
              e.preventDefault();
              if (valido) onConfirmar(limpio);
            }}
            disabled={cargando}
            className={valido ? "gap-2" : "gap-2 opacity-50"}
          >
            {cargando ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Icon name="send" size={16} />
            )}
            Guardar e invitar
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

// ─── Revocar / cancelar invitación ───────────────────────────────────────────

interface DialogoRevocarProps {
  elegidos: Trabajador[] | null;
  cargando: boolean;
  onCancelar: () => void;
  onConfirmar: () => void;
}

function DialogoRevocar({ elegidos, cargando, onCancelar, onConfirmar }: DialogoRevocarProps) {
  if (!elegidos || elegidos.length === 0) return null;

  const uno = elegidos.length === 1 ? elegidos[0] : null;
  /**
   * Cancelar una invitación y revocar un acceso son el mismo DELETE, pero no
   * la misma noticia: en el primero nadie pierde nada, porque nunca llegó a
   * entrar. Prometer "se cerrará la sesión en su teléfono" ahí sería mentira.
   */
  const esCancelacion = elegidos.every((t) => t.licencia.estado === "invitado");

  const titulo = esCancelacion
    ? uno
      ? `Cancelar la invitación de ${nombreCompleto(uno)}`
      : `Cancelar ${elegidos.length} invitaciones`
    : uno
      ? `Revocar el acceso de ${nombreCompleto(uno)}`
      : `Revocar ${elegidos.length} accesos`;

  const cuerpo = esCancelacion
    ? "El enlace que le enviamos deja de funcionar y no podrá crear su contraseña. Puedes volver a invitarlo cuando quieras."
    : uno
      ? "Se cerrará la sesión en su teléfono y no podrá volver a entrar a la app hasta que le asignes otra licencia."
      : "Se cerrarán las sesiones en sus teléfonos y no podrán volver a entrar hasta que les asignes otra licencia.";

  return (
    <AlertDialog open onOpenChange={(abierto) => !abierto && onCancelar()}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{titulo}</AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-3">
              <p>{cuerpo}</p>
              <div className="bg-surface-muted space-y-1.5 rounded-lg border p-3">
                <p className="flex items-start gap-2 text-[11.5px]">
                  <Icon name="task_alt" size={15} className="text-success mt-px" />
                  Sus capacitaciones, firmas y documentos se conservan intactos.
                </p>
                <p className="flex items-start gap-2 text-[11.5px]">
                  <Icon name="key" size={15} className="text-primary-hover mt-px" />
                  {elegidos.length === 1
                    ? "La licencia vuelve a la bolsa y queda disponible al instante."
                    : `Las ${elegidos.length} licencias vuelven a la bolsa y quedan disponibles al instante.`}
                </p>
                <p className="flex items-start gap-2 text-[11.5px]">
                  <Icon name="person_off" size={15} className="text-warning mt-px" />
                  {elegidos.length === 1
                    ? "Sigue en la dotación como trabajador: sólo pierde el acceso a la app."
                    : "Siguen en la dotación como trabajadores: sólo pierden el acceso a la app."}
                </p>
              </div>
              <p className="text-muted-foreground text-xs">
                {esCancelacion
                  ? "No se le avisa por correo."
                  : "No se envía ningún correo al trabajador."}
              </p>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={cargando}>Cancelar</AlertDialogCancel>
          <AlertDialogAction
            onClick={(e) => {
              e.preventDefault();
              onConfirmar();
            }}
            disabled={cargando}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            {cargando ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : esCancelacion ? (
              elegidos.length === 1 ? (
                "Cancelar invitación"
              ) : (
                `Cancelar ${elegidos.length}`
              )
            ) : elegidos.length === 1 ? (
              "Revocar acceso"
            ) : (
              `Revocar ${elegidos.length}`
            )}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
